import React, { useState, useEffect, useCallback, useRef } from "react";
import DonutLabelingMode from "./DonutLabelingMode";
import DonutLabelingBulkVerify from "./DonutLabelingBulkVerify";
import {
  Search,
  RotateCcw,
  Save,
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Cloud,
  CloudOff,
  AlertTriangle,
  Sparkles,
} from "lucide-react";
import FieldSourceBadge from "./FieldSourceBadge";

// 11종 doc_type — v4 학습 데이터는 B/I 만 있지만, 사용자가 잘못 분류된 것을 다른 종류로 재라벨할 수 있도록
// 모든 종류 노출. 부가서류 (BIZ/RID/DL/CONT/SHRG/TAX) 는 v4에 없지만 재분류 후 v5 학습에서 자동 통합.
const DOC_TYPES = [
  { code: "ALL", name: "전체" },
  { code: "B", name: "B · 말소" },
  { code: "I", name: "I · 등록증" },
  { code: "D", name: "D · 폐차" },
  { code: "L", name: "L · 등록원부" },
  { code: "N", name: "N · 경락" },
  { code: "BIZ", name: "BIZ · 사업자" },
  { code: "RID", name: "RID · 주민증" },
  { code: "DL", name: "DL · 운전면허증" },
  { code: "CONT", name: "CONT · 매매계약서" },
  { code: "SHRG", name: "SHRG · 쇼링" },
  { code: "TAX", name: "TAX · 세금계산서" },
];

// 분류 라벨링용 풀 네임 (FormPanel select)
const DOC_TYPE_NAMES = {
  B: "말소등록증",
  I: "자동차등록증",
  D: "폐차인수증명서",
  L: "자동차등록원부",
  N: "경락사실확인서",
  BIZ: "사업자등록증",
  RID: "주민등록증",
  DL: "운전면허증",
  CONT: "매매계약서",
  SHRG: "쇼링내역서",
  TAX: "세금계산서",
};

// 한글 풀네임 → code 역매핑 (이전 데이터의 doc_type 이 한글로 저장된 케이스 정규화)
const DOC_TYPE_NAME_TO_CODE = Object.fromEntries(
  Object.entries(DOC_TYPE_NAMES).map(([code, name]) => [name, code]),
);

// 학습 데이터 실측 (train.jsonl 내 doc_type 종류) — 추가 alias
const DOC_TYPE_ALIASES = {
  // 영문 풀네임
  "Driver's License": "DL",
  "Drivers License": "DL",
  "Car De-registration Certificate": "B",
  "Cancellation Certificate": "B",
  "Vehicle Registration": "I",
  "Vehicle Registration Booklet": "L",
  "Scrap Certificate": "D",
  "Resident Registration Card": "RID",
  "Business Registration": "BIZ",
  // 한글 alias (DOC_TYPE_NAMES 외)
  자동차말소등록사실증명서: "B",
  말소등록사실증명서: "B",
  말소사실증명서: "B",
  등본: "L",
  초본: "L",
};

// 약어/한글/영문 어느 쪽이든 받아 항상 code 로 정규화 (B/I/D/L/N/BIZ/RID/DL/CONT/SHRG/TAX)
function normalizeDocType(value) {
  if (!value) return "";
  const v = String(value).trim();
  if (DOC_TYPE_NAMES[v]) return v; // 이미 code
  if (DOC_TYPE_NAME_TO_CODE[v]) return DOC_TYPE_NAME_TO_CODE[v]; // 한글 standard → code
  if (DOC_TYPE_ALIASES[v]) return DOC_TYPE_ALIASES[v]; // 영문/한글 alias → code
  // 대소문자/공백 무관 fallback
  const vLower = v.toLowerCase();
  for (const [alias, code] of Object.entries(DOC_TYPE_ALIASES)) {
    if (alias.toLowerCase() === vLower) return code;
  }
  return v; // unknown 그대로 (사용자에게 보여줌)
}

// 개인정보 필드 (display 시 마스킹). 학습 GT 자체는 평문 보존.
const SENSITIVE_FIELDS = new Set([
  "jumin_no",
  "owner_id_no",
  "transferor_id",
  "transferee_id",
  "winning_bidder_id",
  "license_no",
]);

// 주민번호 형식: 6자리-7자리. 뒷 6자리 마스킹 (성별 1자리만 노출).
function maskSensitive(key, value) {
  if (!SENSITIVE_FIELDS.has(key) || !value) return value;
  const s = String(value);
  // 이미 마스킹된 경우 그대로
  if (/[\*]{3,}/.test(s)) return s;
  // 주민번호 패턴: 123456-1234567 → 123456-1******
  const m = s.match(/^(\d{6})-(\d)(\d{6})$/);
  if (m) return `${m[1]}-${m[2]}******`;
  // 면허번호 패턴: 16-88-027936-43 → 평문 그대로 (덜 민감)
  return s;
}

// doc_type 별 표준 form 필드 (분류 변경 시 form 자동 통일)
// 차량 양식 (B/I/D/L/N) — 통일 schema 17필드
//   maker 제외: DB 채움률 0% + 운영 vehicleMasterEnricher 가 자동 lookup (학습 불필요)
const VEHICLE_FIELDS = [
  "doc_type",
  "plate_no",
  "vin",
  "car_name",
  "year",
  "owner_name",
  "owner_id_no",
  "owner_address",
  "mileage_km",
  "displacement_cc",
  "vehicle_weight_kg",
  "length_mm",
  "width_mm",
  "height_mm",
  "cbm",
  "canceled_date", // B 말소 / L 등록원부에도 있음
  "bid_amount_krw", // N 낙찰금액 = 매매금액
  "exporter_name", // 수출자/수출화주 (N 인쇄 + B/I/D/L 수기)
  "exporter_biz_no", // 수출자 사업자번호 (10자리)
];
const VEHICLE_DOC_TYPES = new Set(["B", "I", "D", "L", "N"]);

// 양식별 활성 필드 (양식 이미지에 실제 정보가 있는 필드만 입력 가능)
//   비활성 필드 = 회색 readonly + "이 양식에는 없음" placeholder.
//   학습 schema 는 18필드 통일이지만 양식별 채움 가능 필드만 enable → 검수 시 헷갈림 방지.
//
// 2026-05-16: backend `services/docTypeFieldAvailability.js` 와 schema 동기화 필수.
// 변경 시 양쪽 함께 수정.
const FIELD_AVAILABILITY = {
  // 2026-05-24 사용자 필수 필드 정의 (backend docTypeFieldAvailability.js 와 동기화 필수)
  B: new Set([
    "doc_type",
    "plate_no",
    "vin",
    "car_name",
    "year",
    "owner_name",
    "owner_id_no",
    "mileage_km",
    "canceled_date",
    "exporter_name",
    "exporter_biz_no",
  ]),
  I: new Set([
    "doc_type",
    "plate_no",
    "vin",
    "car_name",
    "year",
    "owner_name",
    "owner_id_no",
    "owner_address",
    "displacement_cc",
    "vehicle_weight_kg",
    "length_mm",
    "width_mm",
    "height_mm",
    "exporter_name",
    "exporter_biz_no",
  ]),
  D: new Set([
    "doc_type",
    "plate_no",
    "vin",
    "car_name",
    "year",
    "owner_name",
    "owner_id_no",
    "owner_address",
    "exporter_name",
    "exporter_biz_no",
  ]),
  L: new Set([
    "doc_type",
    "plate_no",
    "vin",
    "car_name",
    "year",
    "owner_name",
    "owner_id_no",
    "owner_address",
    "mileage_km",
    "canceled_date",
    "exporter_name",
    "exporter_biz_no",
  ]),
  // N 경락사실확인서: owner_* = 매도자(현 등록 소유자), exporter_* = 매수자(수출자/수출화주)
  // 양식에 인쇄된 모든 필드 활성화 (2026-05-16 사용자 검증, 이미지 #8/#10/#11)
  N: new Set([
    "doc_type",
    "plate_no",
    "vin",
    "car_name",
    "year",
    "owner_name",
    "owner_id_no",
    "owner_address",
    "mileage_km",
    "displacement_cc",
    "bid_amount_krw",
    "exporter_name",
    "exporter_biz_no",
  ]),
};
function isFieldDisabled(docType, fieldKey) {
  if (!VEHICLE_DOC_TYPES.has(docType)) return false;
  const allowed = FIELD_AVAILABILITY[docType];
  if (!allowed) return false;
  return !allowed.has(fieldKey);
}
const DOC_TYPE_FIELDS = {
  B: VEHICLE_FIELDS,
  I: VEHICLE_FIELDS,
  D: VEHICLE_FIELDS,
  L: VEHICLE_FIELDS,
  N: VEHICLE_FIELDS,
  // 비-차량 양식 — 별도 schema
  CONT: [
    "doc_type",
    "vin",
    "plate_no",
    "car_name",
    "transferor_name",
    "transferor_id",
    "transferee_name",
    "transferee_id",
    "contract_date",
    "contract_amount",
  ],
  BIZ: [
    "doc_type",
    "biz_no",
    "company_name",
    "owner_name",
    "business_type",
    "registered_date",
    "address",
  ],
  RID: ["doc_type", "owner_name", "jumin_no", "address", "issued_date"],
  DL: [
    "doc_type",
    "owner_name",
    "jumin_no",
    "license_no",
    "address",
    "issued_date",
  ],
  SHRG: [
    "doc_type",
    "vin",
    "car_name",
    "shipper",
    "consignee",
    "port_of_loading",
    "port_of_discharge",
  ],
  TAX: ["doc_type", "biz_no", "company_name", "tax_amount", "issue_date"],
};

const SPLIT_FILTERS = [
  { value: "all", label: "전체" },
  { value: "train", label: "train" },
  { value: "val", label: "val" },
  { value: "test", label: "test" },
];

const SOURCE_FILTERS = [
  { value: "all", label: "source 전체" },
  { value: "user_label", label: "사용자 라벨" },
  { value: "user_label_ref", label: "🆕 reference 147건" },
  { value: "v2_distill", label: "v2 distill" },
];

// 2026-05-24 정리: 8개 → 6개 (중복/0건/불필요 옵션 제거)
//   제거: empty_extraction (0건 — manifest 의 모든 sample 에 1+ 필드 있음)
//         matches (정상 sample — 검수 흐름 무관)
//         missing_canceled_date (core_missing 의 B 양식 부분집합)
const CLASSIFY_FILTERS = [
  { value: "all", label: "키워드 분류 전체" },
  { value: "core_missing", label: "🆘 핵심 필드 결손 (AI 검증추출 대상)" },
  { value: "suspect", label: "⚠️ 예측 ≠ 라벨 (doc_type 검수)" },
  { value: "no_match", label: "키워드 매칭 실패 (doc_type 미정)" },
  { value: "unprocessed", label: "미처리 (자동 분류 필요)" },
  { value: "masked_pii", label: "🔒 민감 필드 마스킹 (평문 입력)" },
];

const FIELD_LABELS = {
  // 차량 (B/I/D/L/N)
  vin: "차대번호 (VIN, 17자) ⭐",
  plate_no: "자동차등록번호",
  car_name: "차명 (영문)",
  maker: "제조사",
  year: "모델연도 (4자리)",
  mileage_km: "주행거리 (km)",
  owner_name: "소유자명 ⭐",
  owner_id_no: "주민/법인번호",
  owner_address: "사용본거지/주소",
  displacement_cc: "배기량 (cc)",
  vehicle_weight_kg: "총중량 (kg)",
  length_mm: "가로 (mm)",
  width_mm: "세로 (mm)",
  height_mm: "높이 (mm)",
  cbm: "CBM",
  bid_amount_krw: "낙찰금액 / 매매금액 (KRW)",
  exporter_name: "수출자 / 수출화주 ⭐",
  exporter_biz_no: "수출자 사업자번호 (10자리)",
  canceled_date: "말소등록일",
  registration_date: "최초등록일",
  issue_date: "발급일",
  doc_subtype: "갑/을 (등본/초본)",
  // 사업자등록증 (BIZ)
  biz_no: "사업자번호 (10자리) ⭐",
  company_name: "상호 ⭐ (은행 입금자명 매칭키)",
  business_type: "업종",
  registered_date: "개업/등록일",
  address: "주소",
  // 주민증/면허증 (RID)
  jumin_no: "주민번호 (마스킹) ⭐",
  license_no: "면허번호",
  issued_date: "발급일",
  // 매매계약서 (CONT)
  transferor_name: "양도자(매도) 성명 ⭐",
  transferor_id: "양도자 주민/사업자번호",
  transferee_name: "양수자(매수) 성명 ⭐",
  transferee_id: "양수자 주민/사업자번호",
  contract_date: "계약일",
  contract_amount: "계약금액",
  // 경락사실확인서 (N)
  winning_bidder_name: "경락자(매수) 성명 ⭐",
  winning_bidder_id: "경락자 주민/사업자번호",
  winning_amount: "경락금액",
  auction_date: "경매일",
  // 폐차 (D)
  scrap_date: "폐차일자",
  scrap_company: "폐차업체",
};
const fieldLabel = (k) => FIELD_LABELS[k] || k;

function flatten(obj) {
  const out = {};
  if (!obj || typeof obj !== "object") return out;
  for (const [k, v] of Object.entries(obj)) {
    if (k === "extra" && v && typeof v === "object") {
      for (const [ek, ev] of Object.entries(v)) out[`extra.${ek}`] = ev;
    } else out[k] = v;
  }
  return out;
}

// ──────────────────────────────────────────────────────────
// 가운데 이미지 뷰어
// ──────────────────────────────────────────────────────────
function ImageViewer({ url, onClose }) {
  const [scale, setScale] = useState(1);
  useEffect(() => {
    setScale(1);
  }, [url]);
  if (!url) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-gray-400 bg-gray-50">
        샘플을 선택하세요
      </div>
    );
  }
  return (
    <div className="flex-1 flex flex-col bg-gray-100 min-w-0">
      <div className="px-3 py-1.5 border-b bg-white flex items-center gap-2 text-xs">
        <div className="flex-1" />
        <button
          onClick={() =>
            setScale((s) => Math.max(0.25, +(s - 0.25).toFixed(2)))
          }
          className="p-1 rounded hover:bg-gray-100"
          title="축소"
        >
          <ZoomOut className="w-3.5 h-3.5" />
        </button>
        <span className="text-gray-700 tabular-nums w-12 text-center">
          {Math.round(scale * 100)}%
        </span>
        <button
          onClick={() => setScale((s) => Math.min(5, +(s + 0.25).toFixed(2)))}
          className="p-1 rounded hover:bg-gray-100"
          title="확대"
        >
          <ZoomIn className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => setScale(1)}
          className="p-1 rounded hover:bg-gray-100"
          title="원본"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100"
            title="닫기"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="flex-1 overflow-auto bg-gray-50 p-4">
        <div className="flex justify-center">
          <img
            src={url}
            alt=""
            style={{
              transform: `scale(${scale})`,
              transformOrigin: "top center",
              maxWidth: scale <= 1 ? "100%" : "none",
            }}
            className="bg-white shadow-sm transition-transform"
          />
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// 우측 GT 편집 폼
// ──────────────────────────────────────────────────────────
function FormPanel({
  sample,
  onClose,
  onSaved,
  aiReviewMeta,
  correctionStatus,
  onGtChange,
}) {
  const [editGt, setEditGt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState("");
  const [focusedField, setFocusedField] = useState(null);
  // Popup state: { field: string } | null
  const [aiPopup, setAiPopup] = useState(null);
  const popupRef = useRef(null);
  const triggerRefs = useRef({});

  // Close popup on Escape
  useEffect(() => {
    if (!aiPopup) return;
    const handler = (e) => {
      if (e.key === "Escape") setAiPopup(null);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [aiPopup]);

  // Close popup on outside click
  useEffect(() => {
    if (!aiPopup) return;
    const onPointerDown = (e) => {
      const popup = popupRef.current;
      const trigger = triggerRefs.current?.[aiPopup.field];
      if (popup && popup.contains(e.target)) return;
      if (trigger && trigger.contains(e.target)) return;
      setAiPopup(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [aiPopup]);

  // Close popup when navigating to a different sample
  useEffect(() => {
    setAiPopup(null);
  }, [sample?.id]);

  useEffect(() => {
    if (sample) {
      const initial = JSON.parse(JSON.stringify(sample.gt || {}));
      // GT 에 doc_type 없으면 sample.doc_type (top-level) 으로 fallback (manifest only sample 호환)
      if (!initial.doc_type && sample.doc_type)
        initial.doc_type = sample.doc_type;
      // doc_type 한글 → code 자동 정규화 (이전 데이터 호환)
      if (initial.doc_type)
        initial.doc_type = normalizeDocType(initial.doc_type);
      setEditGt(initial);
      setNotes("");
    }
  }, [sample?.id]);

  if (!sample) return null;

  const editFlat = flatten(editGt || {});
  const origFlat = flatten(sample.gt || {});
  // 현재 선택된 doc_type 의 표준 필드 + 실제 추출/원본 필드 union
  // → 분류 변경 시 form 자동 통일 (누락 필드는 빈 값, 사용자가 채움)
  const _curDt = (editGt && editGt.doc_type) || sample.doc_type || "";
  const _stdKeys = DOC_TYPE_FIELDS[_curDt] || [];
  // 모든 양식 std 키만 표시 (schema 외 GT 키 숨김 — 다른 양식 GT 가 섞여 있어도 표시 X).
  // 저장 시 GT 의 extra 키는 그대로 보존 (데이터 손실 방지).
  // 단 schema 미정의 양식은 union (fallback).
  const allKeys =
    _stdKeys.length > 0
      ? _stdKeys
      : Array.from(
          new Set([...Object.keys(editFlat), ...Object.keys(origFlat)]),
        );

  const setEditField = (key, val) => {
    const next = { ...(editGt || {}) };
    // doc_type 필드는 항상 code 로 정규화 (한글 입력해도 code 저장)
    const normalizedVal = key === "doc_type" ? normalizeDocType(val) : val;
    if (key.startsWith("extra.")) {
      next.extra = { ...(next.extra || {}), [key.slice(6)]: normalizedVal };
    } else next[key] = normalizedVal;
    setEditGt(next);
    // H: 부모에게 GT 변경 통지 (pendingEdits 자동 보존)
    onGtChange?.(sample.id, next);
  };

  const _userId =
    (typeof window !== "undefined" &&
      (localStorage.getItem("userId") || sessionStorage.getItem("userId"))) ||
    "";

  const removeSample = async () => {
    const reason = window.prompt(
      "삭제 사유 (선택, 학습 빌드 시 제외됨):",
      notes || "",
    );
    if (reason === null) return; // cancel
    setSaving(true);
    try {
      const r = await fetch(
        `/api/donut-eval/samples/${encodeURIComponent(sample.id)}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json", "X-User-Id": _userId },
          body: JSON.stringify({ reason }),
        },
      );
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "delete failed");
      onSaved && onSaved({ ...j, deleted: true, id: sample.id });
    } catch (e) {
      alert(`삭제 실패: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(
        `/api/donut-eval/samples/${encodeURIComponent(sample.id)}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json", "X-User-Id": _userId },
          // G.4: status='confirmed' 명시 — 사용자가 GT 저장 클릭 = 검수 확정
          body: JSON.stringify({ gt: editGt, notes, status: "confirmed" }),
        },
      );
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "save failed");
      // G.2: savedId 같이 올려보내기 (onSaved 핸들러가 list 갱신 + 다음 선택에 사용)
      onSaved && onSaved({ ...j, savedId: sample.id });
      // G.4: alert 제거 — 배너 표시는 부모 onSaved 핸들러에서 처리
    } catch (e) {
      alert(`저장 실패: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // doc_type 한글/code 어느 쪽이든 normalize (이전 데이터 대비)
  const _rawCurDt = (editGt && editGt.doc_type) || sample.doc_type || "";
  const currentDocType = normalizeDocType(_rawCurDt);
  const _origDt = normalizeDocType(sample.doc_type || "");
  const docChanged = currentDocType !== _origDt;

  const setDocType = (newCode) => {
    // 항상 code 로 저장 (한글로 저장 X, 학습 빌드 시 unknown class 방지)
    const next = { ...(editGt || {}), doc_type: normalizeDocType(newCode) };
    setEditGt(next);
    // H: 부모에게 doc_type 변경 통지
    onGtChange?.(sample.id, next);
  };

  // AI 검증추출 메타 helpers
  const suspectSet = new Set(aiReviewMeta?.suspect_fields || []);
  const hasMeta = !!aiReviewMeta && suspectSet.size > 0;
  const isPending = correctionStatus === "pending";

  return (
    <div className="w-[380px] flex-shrink-0 border-l bg-white flex flex-col">
      {/* 헤더 + 분류 통합 (한 줄, 압축) */}
      <div className="px-3 py-2 border-b flex items-center gap-2">
        <select
          value={currentDocType}
          onChange={(e) => setDocType(e.target.value)}
          className={`text-xs px-2 py-1 rounded border outline-none focus:border-blue-500 ${
            docChanged
              ? "border-amber-400 bg-amber-50 text-amber-800 font-medium"
              : "border-blue-300 bg-blue-50 text-blue-700 font-medium"
          }`}
          title={
            docChanged
              ? `변경됨: ${sample.doc_type} → ${currentDocType}`
              : "문서 분류"
          }
        >
          {Object.entries(DOC_TYPE_NAMES).map(([code, nm]) => (
            <option key={code} value={code}>
              {code} · {nm}
            </option>
          ))}
        </select>
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-600 border border-gray-200">
          {sample.split}
        </span>
        {sample.source === "user_label" && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
            user
          </span>
        )}
        <span
          className="ml-auto text-[10px] font-mono text-gray-400 truncate max-w-[100px]"
          title={sample.id}
        >
          {sample.id}
        </span>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-gray-100 text-gray-500"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* AI 검증대기 배너 */}
      {isPending && hasMeta && (
        <div className="px-3 py-1.5 bg-yellow-50 border-b border-yellow-200 text-[11px] text-yellow-800 flex items-center gap-1.5">
          <span>AI 검증대기</span>
          <span className="font-medium">{suspectSet.size}개 의심 필드</span>
          <span className="text-yellow-600">(Donut/Gemini 불일치)</span>
        </div>
      )}

      <div className="flex-1 overflow-auto px-3 py-2 space-y-1">
        {allKeys.length === 0 && (
          <div className="text-xs text-gray-400 text-center py-6">
            필드 없음
          </div>
        )}
        {allKeys.map((k) => {
          const editV = editFlat[k];
          const origV = origFlat[k];
          const changed = String(editV ?? "") !== String(origV ?? "");
          const isSensitive = SENSITIVE_FIELDS.has(k);
          const focused = focusedField === k;
          const disabled = isFieldDisabled(_curDt, k);
          const isSuspect = !disabled && hasMeta && suspectSet.has(k);
          const popupOpen = aiPopup?.field === k;
          // 민감 필드는 평소 마스킹, focus 시 평문 (편집 위해)
          const displayV =
            isSensitive && !focused
              ? maskSensitive(k, editV)
              : editV == null
                ? ""
                : String(editV);
          return (
            <div
              key={k}
              className={`grid grid-cols-12 gap-2 items-center ${disabled ? "opacity-40" : ""}`}
            >
              <label className="col-span-5 text-[11px] text-gray-700 flex items-center gap-1 min-w-0">
                <span
                  className={`w-1 h-1 rounded-full flex-shrink-0 ${changed ? "bg-blue-500" : "bg-gray-300"}`}
                />
                <span className="truncate" title={fieldLabel(k)}>
                  {fieldLabel(k)}
                </span>
                {isSensitive && (
                  <span
                    className="text-[9px] text-gray-400 flex-shrink-0"
                    title="민감 필드 — 클릭 시 평문 편집"
                  >
                    🔒
                  </span>
                )}
              </label>
              <div className="col-span-6 min-w-0 relative">
                <input
                  className={`w-full text-xs px-2 py-1 border rounded outline-none ${
                    disabled
                      ? "bg-gray-100 border-gray-200 cursor-not-allowed text-gray-400"
                      : isSuspect
                        ? "border-red-400 focus:border-red-500 focus:ring-1 focus:ring-red-100"
                        : "border-gray-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                  }`}
                  placeholder={
                    disabled
                      ? "이 양식에는 없음"
                      : origV != null
                        ? isSensitive
                          ? maskSensitive(k, origV)
                          : String(origV)
                        : ""
                  }
                  value={displayV}
                  onChange={(e) => setEditField(k, e.target.value)}
                  onFocus={() => setFocusedField(k)}
                  onBlur={() => setFocusedField(null)}
                  disabled={disabled}
                  title={
                    disabled
                      ? `이 양식 (${_curDt}) 에는 없는 필드`
                      : isSuspect
                        ? `의심 필드: Donut="${aiReviewMeta?.donut?.[k] ?? ""}" / Gemini="${aiReviewMeta?.gemini?.[k] ?? ""}"`
                        : changed && origV != null
                          ? `원본: ${isSensitive ? maskSensitive(k, origV) : origV}`
                          : undefined
                  }
                />
                {/* AI suspect popup (absolute positioned inline) */}
                {isSuspect && popupOpen && (
                  <div
                    ref={popupRef}
                    className="absolute right-0 top-full mt-1 z-50 bg-white border border-red-300 rounded shadow-lg p-2 w-64 text-[11px]"
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <div className="text-red-700 font-medium mb-1.5">
                      AI 불일치 필드
                    </div>
                    <div className="space-y-1 mb-2">
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500 w-14 flex-shrink-0">
                          Donut:
                        </span>
                        <span className="font-mono text-gray-800 truncate">
                          {String(aiReviewMeta?.donut?.[k] ?? "(없음)")}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500 w-14 flex-shrink-0">
                          Gemini:
                        </span>
                        <span className="font-mono text-gray-800 truncate">
                          {String(aiReviewMeta?.gemini?.[k] ?? "(없음)")}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        className="flex-1 px-2 py-1 rounded bg-blue-100 text-blue-800 hover:bg-blue-200 font-medium"
                        onClick={() => {
                          setEditField(k, aiReviewMeta?.donut?.[k] ?? "");
                          setAiPopup(null);
                        }}
                      >
                        D 채택
                      </button>
                      <button
                        className="flex-1 px-2 py-1 rounded bg-green-100 text-green-800 hover:bg-green-200 font-medium"
                        onClick={() => {
                          setEditField(k, aiReviewMeta?.gemini?.[k] ?? "");
                          setAiPopup(null);
                        }}
                      >
                        G 채택
                      </button>
                      <button
                        className="px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
                        onClick={() => setAiPopup(null)}
                      >
                        닫기
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {/* suspect icon OR OCR badge */}
              <div className="col-span-1 flex items-center justify-end">
                {isSuspect ? (
                  <button
                    ref={(el) => {
                      triggerRefs.current[k] = el;
                    }}
                    className="text-[13px] leading-none hover:opacity-70"
                    title="AI 불일치 — 클릭하여 Donut/Gemini 값 비교"
                    onClick={() => setAiPopup(popupOpen ? null : { field: k })}
                  >
                    ⚠️
                  </button>
                ) : (
                  <FieldSourceBadge
                    fieldKey={k}
                    value={editV}
                    rawValue={sample?._raw?.[k]}
                    dbAugmented={sample?._enrichment?.db_augmented?.[k]}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-3 py-2 border-t flex items-center gap-2 bg-white">
        <input
          type="text"
          className="flex-1 text-xs px-2 py-1 border border-gray-200 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-100 outline-none"
          placeholder="메모 (선택)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <button
          onClick={removeSample}
          disabled={saving}
          className="px-2 py-1 text-xs rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40"
          title="이 샘플을 학습 데이터에서 제외 (corrections 에 deleted 마커)"
        >
          삭제
        </button>
        <button
          onClick={onClose}
          className="px-3 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 text-gray-700"
        >
          닫기
        </button>
        <button
          onClick={save}
          disabled={saving || !editGt}
          className="px-3 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1 font-medium"
        >
          <Save className="w-3 h-3" /> {saving ? "저장중" : "GT 저장"}
        </button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// 메인
// ──────────────────────────────────────────────────────────
export default function DonutLearningDataPanel() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);

  const [docType, setDocType] = useState("ALL");
  const [split, setSplit] = useState("all");
  const [source, setSource] = useState("all");
  const [classify, setClassify] = useState("all");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);

  const [corrCount, setCorrCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [pilotRunning, setPilotRunning] = useState(false);
  const [pilotResult, setPilotResult] = useState(null);
  const [pilotProgress, setPilotProgress] = useState({ done: 0, total: 0 });

  // ── 다중선택 + 라벨링 모드 ──
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [labelingIds, setLabelingIds] = useState(null); // string[] | null

  // ── 일괄 AI 자동확정 ──
  const [bulkConfirm, setBulkConfirm] = useState({
    running: false,
    processed: 0,
    total: 0,
    result: null,
  });
  // IDs processed in the last bulk-extract-confirm (green tint for 3s)
  const [recentlyProcessed, setRecentlyProcessed] = useState(new Set());
  // ── AI 검증추출 (staging) ──
  const [bulkVerifyIds, setBulkVerifyIds] = useState(null); // kept for modal compat (unused)
  const [stageRunning, setStageRunning] = useState(false);
  const [recentlyStaged, setRecentlyStaged] = useState(new Set());
  // G.4: 저장 완료 배너 (alert 대체)
  const [stageBanner, setStageBanner] = useState(null); // { text, type } | null
  // H: pendingEdits — 사용자가 수정한 staged sample 의 gt 임시 보존 (backend 호출 X)
  //   Map<sample_id, gt>  (수정한 sample 만 저장됨)
  const [pendingEdits, setPendingEdits] = useState(new Map());

  // ── v10 학습 트리거 ──
  const [trainModal, setTrainModal] = useState(false); // 확인 모달 표시
  const [trainJob, setTrainJob] = useState(null); // { job_id, status, ... }
  const [trainPolling, setTrainPolling] = useState(false);
  const trainPollRef = useRef(null);
  const [evalSwapModal, setEvalSwapModal] = useState(false); // 평가+swap 안내 모달

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === items.length && items.every((it) => prev.has(it.id))) {
        return new Set();
      }
      return new Set(items.map((it) => it.id));
    });
  };

  const runBulkExtractConfirm = async () => {
    if (bulkConfirm.running || selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    setBulkConfirm({
      running: true,
      processed: 0,
      total: ids.length,
      result: null,
    });
    try {
      const r = await fetch("/api/donut/labeling/bulk-extract-confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids, mode: "agree_only" }),
      });
      const j = await r.json();
      setBulkConfirm({
        running: false,
        processed: j.processed || 0,
        total: ids.length,
        result: j,
      });
      if (j.ok) {
        // Mark processed IDs for green tint, auto-clear after 3s
        const okIds = new Set(
          (j.details || []).filter((d) => d.status === "ok").map((d) => d.id),
        );
        if (okIds.size > 0) {
          setRecentlyProcessed(okIds);
          setTimeout(() => setRecentlyProcessed(new Set()), 3000);
        }
        // Refresh list + corrections counter
        setSelectedIds(new Set());
        await Promise.all([reloadCorrCount(), reloadList()]);
        alert(
          `AI 자동확정 완료\n처리: ${j.processed}건 / 건너뜀: ${j.skipped}건 / DB 저장: ${j.upserted}건`,
        );
      } else {
        alert(`오류: ${j.error || "unknown error"}`);
      }
    } catch (e) {
      setBulkConfirm({
        running: false,
        processed: 0,
        total: ids.length,
        result: null,
      });
      alert(`AI 자동확정 실패: ${e.message}`);
    }
  };

  const runBulkExtractStage = async () => {
    if (stageRunning || selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    if (ids.length > 50) {
      alert("최대 50건까지 가능합니다.");
      return;
    }
    setStageRunning(true);
    // Show progress hint (non-blocking)
    const estSec = ids.length * 3;
    const bannerMsg = `AI 추출 중... ${ids.length}건 × 3초 ≈ ${estSec}초`;
    // We just use console; a banner in JSX below will pick up stageRunning
    console.log(`[bulk-extract-stage] starting: ${bannerMsg}`);
    try {
      const r = await fetch("/api/donut/labeling/bulk-extract-stage", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "stage failed");
      const suspectCount = (j.summary || []).filter(
        (s) => s.suspect_count > 0,
      ).length;
      // Yellow tint for staged IDs (10초 — 검수 동안 시각 표시 유지)
      const stagedIdsArr = (j.summary || [])
        .filter((s) => !s.error)
        .map((s) => s.id);
      const stagedIds = new Set(stagedIdsArr);
      if (stagedIds.size > 0) {
        setRecentlyStaged(stagedIds);
        // G.4: 60초로 연장 (검수 시간 충분히 확보). 저장 시 개별 제거됨.
        setTimeout(() => setRecentlyStaged(new Set()), 60000);
      }
      setSelectedIds(new Set());
      await Promise.all([reloadCorrCount(), reloadList()]);
      // F.3: if currently-open detail was just staged, refetch it so the
      // right panel shows the new AI-extracted GT instead of stale data.
      if (selectedId && stagedIds.has(selectedId)) {
        fetchDetail(selectedId);
      } else if (stagedIdsArr.length > 0) {
        // 추출 후 첫 staged sample 자동 선택 → 우측 detail 즉시 표시 (검수 시작)
        setSelectedId(stagedIdsArr[0]);
      }
      // G.4: alert 제거 → 비차단 배너로 교체
      const bannerText = `AI 추출 완료 — ${j.staged}건 staged${
        suspectCount > 0 ? ` · 의심 필드 ${suspectCount}건` : ""
      } · 우측 패널에서 확인 후 GT 저장`;
      setStageBanner({ text: bannerText, type: "info" });
      setTimeout(() => setStageBanner(null), 5000);
    } catch (e) {
      alert(`AI 검증추출 실패: ${e.message}`);
    } finally {
      setStageRunning(false);
    }
  };

  // ── H: pendingEdits 콜백 ──────────────────────────────────────────────
  // FormPanel 의 onGtChange prop 으로 호출됨 — staged sample 수정 시 Map 에 보존
  const handleFieldEdit = useCallback((sampleId, newGt) => {
    setPendingEdits((prev) => new Map(prev).set(sampleId, newGt));
  }, []);

  // ── H: 일괄 저장 ──────────────────────────────────────────────────────
  const handleBulkSave = async () => {
    const stagedIds = Array.from(recentlyStaged);
    if (stagedIds.length === 0) return;
    const editsObj = Object.fromEntries(pendingEdits);
    setStageBanner({ text: `${stagedIds.length}건 저장 중...`, type: "info" });
    try {
      const r = await fetch("/api/donut/labeling/bulk-confirm-staged", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: stagedIds, edits: editsObj }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "bulk save failed");
      // 성공: staged + pendingEdits 비우기
      setRecentlyStaged(new Set());
      setPendingEdits(new Map());
      setSelectedId(null);
      await Promise.all([reloadStats(), reloadList()]);
      setStageBanner({
        text: `${j.saved}건 confirmed 저장 완료`,
        type: "success",
      });
      setTimeout(() => setStageBanner(null), 3000);
    } catch (e) {
      setStageBanner({ text: `저장 실패: ${e.message}`, type: "error" });
    }
  };

  // ── v10 학습 트리거 헬퍼 ──────────────────────────────────────────────
  const TRAIN_STATUS_LABELS = {
    queued: "대기 중",
    building_jsonl: "JSONL 빌드 중",
    uploading: "업로드 중",
    training_started: "학습 시작 중",
    awaiting_train_script: "학습 스크립트 대기",
    kicked_off: "킥오프 완료",
    training: "학습 중",
    completed: "학습 완료",
    failed: "실패",
    skipped: "건너뜀 (lock)",
  };

  const stopTrainPoll = useCallback(() => {
    if (trainPollRef.current) {
      clearInterval(trainPollRef.current);
      trainPollRef.current = null;
    }
    setTrainPolling(false);
  }, []);

  const startTrainPoll = useCallback(
    (jobId) => {
      if (trainPollRef.current) clearInterval(trainPollRef.current);
      const start = Date.now();
      const MAX_POLL_MS = 5 * 60 * 1000; // 5분
      setTrainPolling(true);
      trainPollRef.current = setInterval(async () => {
        if (Date.now() - start > MAX_POLL_MS) {
          stopTrainPoll();
          return;
        }
        try {
          const r = await fetch(
            `/api/donut/labeling/train-status?job_id=${encodeURIComponent(jobId)}`,
          );
          if (!r.ok) return;
          const j = await r.json();
          setTrainJob(j);
          if (
            j.status === "kicked_off" ||
            j.status === "completed" ||
            j.status === "failed" ||
            j.status === "skipped" ||
            j.status === "awaiting_train_script" ||
            j.is_complete === true
          ) {
            stopTrainPoll();
          }
        } catch {
          /* ignore transient errors */
        }
      }, 5000);
    },
    [stopTrainPoll],
  );

  const triggerTrain = async () => {
    try {
      const r = await fetch("/api/donut/labeling/trigger-train", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirm: true }),
      });
      const j = await r.json();
      if (!r.ok) {
        if (j.error === "insufficient_confirmed") {
          alert(
            `confirmed 샘플 부족: ${j.count}건 (최소 ${j.threshold}건 필요)`,
          );
        } else {
          alert(`학습 트리거 실패: ${j.message || j.error || "unknown"}`);
        }
        return;
      }
      setTrainJob({
        job_id: j.job_id,
        status: "queued",
        confirmed_count: j.confirmed_count,
      });
      setTrainModal(false);
      startTrainPoll(j.job_id);
    } catch (e) {
      alert(`오류: ${e.message}`);
    }
  };

  const reloadStats = useCallback(async () => {
    try {
      const r = await fetch("/api/donut-eval/stats");
      setStats(await r.json());
    } catch (e) {
      console.error(e);
    }
  }, []);

  const reloadCorrCount = useCallback(async () => {
    try {
      const r = await fetch("/api/donut-eval/corrections");
      const j = await r.json();
      setCorrCount(j.total || 0);
    } catch {
      /* ignore */
    }
  }, []);

  const reloadPilotProgress = useCallback(async () => {
    try {
      const r = await fetch("/api/donut-eval/pilot-progress");
      const j = await r.json();
      setPilotProgress({ done: j.done || 0, total: j.total || 0 });
    } catch {
      /* ignore */
    }
  }, []);

  const reloadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        split,
        source,
      });
      if (docType && docType !== "ALL") params.set("type", docType);
      if (classify && classify !== "all") params.set("classify", classify);
      if (q) params.set("q", q);
      const r = await fetch(`/api/donut-eval/samples?${params}`);
      const j = await r.json();
      setItems(j.items || []);
      setTotal(j.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, split, source, docType, classify, q]);

  useEffect(() => {
    reloadStats();
    reloadCorrCount();
    reloadPilotProgress();
  }, [reloadStats, reloadCorrCount, reloadPilotProgress]);
  useEffect(() => {
    reloadList();
  }, [reloadList]);

  const fetchDetail = useCallback((id) => {
    if (!id) {
      setDetail(null);
      return;
    }
    fetch(`/api/donut-eval/samples/${encodeURIComponent(id)}`)
      .then((r) => r.json())
      .then(setDetail)
      .catch(console.error);
  }, []);

  useEffect(() => {
    fetchDetail(selectedId);
  }, [selectedId, fetchDetail]);

  // ──────────── 키보드 네비게이션 ────────────
  // ↑/↓ : 한 칸, PageUp/PageDown : 10칸, Home/End : 처음/끝
  // 마지막 항목에서 ↓ : 다음 페이지로 이동 (있으면), 처음 항목에서 ↑ : 이전 페이지
  // input/textarea/select focus 중에는 동작 안 함
  const itemRefs = useRef({});
  useEffect(() => {
    const handler = (e) => {
      if (bulkVerifyIds) return; // BulkVerify 모달 활성 시 parent 단축키 비활성
      const tag = (e.target.tagName || "").toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const navKeys = [
        "ArrowDown",
        "ArrowUp",
        "PageDown",
        "PageUp",
        "Home",
        "End",
        // G.5: J/K vim-style navigation aliases
        "j",
        "J",
        "k",
        "K",
      ];
      if (!navKeys.includes(e.key)) return;
      if (items.length === 0) return;
      e.preventDefault();
      const idx = items.findIndex((it) => it.id === selectedId);
      let next = idx;
      const tp = Math.max(1, Math.ceil(total / pageSize));
      // G.5: J/K = ArrowDown/ArrowUp aliases
      const effectiveKey =
        e.key === "j" || e.key === "J"
          ? "ArrowDown"
          : e.key === "k" || e.key === "K"
            ? "ArrowUp"
            : e.key;
      if (effectiveKey === "ArrowDown") {
        if (idx < 0) next = 0;
        else if (idx >= items.length - 1) {
          if (page < tp) {
            setPage((p) => p + 1);
            return;
          }
          next = items.length - 1;
        } else next = idx + 1;
      } else if (effectiveKey === "ArrowUp") {
        if (idx < 0) next = 0;
        else if (idx === 0) {
          if (page > 1) {
            setPage((p) => p - 1);
            return;
          }
          next = 0;
        } else next = idx - 1;
      } else if (e.key === "PageDown") {
        next = Math.min(items.length - 1, (idx < 0 ? 0 : idx) + 10);
      } else if (e.key === "PageUp") {
        next = Math.max(0, (idx < 0 ? 0 : idx) - 10);
      } else if (e.key === "Home") {
        next = 0;
      } else if (e.key === "End") {
        next = items.length - 1;
      }
      if (next !== idx) {
        const id = items[next].id;
        setSelectedId(id);
        // 다음 tick 에 스크롤 (DOM 업데이트 후)
        setTimeout(() => {
          const el = itemRefs.current[id];
          if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }, 0);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [items, selectedId, page, total, pageSize, bulkVerifyIds]);

  // 페이지 이동 후 자동으로 첫 항목 선택 (화살표 keep going 가능)
  const lastPageRef = useRef(page);
  useEffect(() => {
    if (lastPageRef.current !== page && items.length > 0) {
      // 페이지가 바뀌었으면 첫 항목 자동 선택 (다음 페이지로 갔을 때) 또는 마지막 (이전 페이지로 갔을 때)
      // 단, 사용자가 페이지 버튼 직접 클릭한 경우는 영향 작음
      const wentDown = page > lastPageRef.current;
      const target = wentDown ? items[0] : items[items.length - 1];
      if (target) {
        setSelectedId(target.id);
        setTimeout(() => {
          const el = itemRefs.current[target.id];
          if (el) el.scrollIntoView({ block: "nearest", behavior: "auto" });
        }, 0);
      }
      lastPageRef.current = page;
    }
  }, [page, items]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Q6: 페이지 변경 시 recentlyStaged 초기화 (다른 페이지 id 가 잔류하지 않도록)
  useEffect(() => {
    setRecentlyStaged(new Set());
  }, [page]);

  const reloadCache = async () => {
    try {
      const r = await fetch("/api/donut-eval/reload", { method: "POST" });
      const j = await r.json();
      if (j.ok) await Promise.all([reloadStats(), reloadList()]);
      else alert(`로드 실패: ${j.error}`);
    } catch (e) {
      alert(`오류: ${e.message}`);
    }
  };

  const runPilotClassify = async () => {
    if (pilotRunning) return;
    const doneNow = pilotProgress.done;
    const totalNow = pilotProgress.total;
    const promptMsg =
      doneNow > 0
        ? `이미 ${doneNow}/${totalNow}건 분류됨.\n\n추가로 분류할 N개? (다음 N개를 결정론적 순서로 진행)\n• N=100 → ${doneNow + 1}~${doneNow + 100}번째`
        : `Pilot 분류할 N개? (전체 ${totalNow}건 중)\n• 결정론적 id-hash 순서 — 매 호출마다 같은 순서`;
    const n = parseInt(prompt(promptMsg, "100") || "0");
    if (!n || n < 1) return;
    const method = window.prompt(
      `분류 방법 선택:\n  1) keyword  — PaddleOCR + 키워드 매칭 (server, 외부 X, 가장 정확)\n  2) donut    — donut /classify (B/I만 학습, D/L 분류 한계)\n  3) gemini   — Gemini Vision (외부 API, ~$0.025/100건)\n  4) all      — 셋 다 비교\n\n번호 입력`,
      "1",
    );
    if (!method) {
      return;
    }
    const useKeyword = ["1", "4", "keyword", "all"].includes(method);
    const useDonut = ["2", "4", "donut", "all"].includes(method);
    const useGemini = ["3", "4", "gemini", "all"].includes(method);
    setPilotRunning(true);
    setPilotResult(null);
    try {
      const params = new URLSearchParams({
        n: String(n),
        keyword: useKeyword ? "true" : "false",
        donut: useDonut ? "true" : "false",
        gemini: useGemini ? "true" : "false",
        concurrency: "6",
      });
      const r = await fetch(`/api/donut-eval/classify-pilot?${params}`, {
        method: "POST",
      });
      const j = await r.json();
      setPilotResult(j);
      await reloadPilotProgress();
    } catch (e) {
      alert(`Pilot 실행 실패: ${e.message}`);
    } finally {
      setPilotRunning(false);
    }
  };

  const bulkApplySuspects = async () => {
    if (!stats?.keyword_classify?.suspect)
      return alert("적용할 suspect 가 없습니다.");
    const dry = await fetch("/api/donut-eval/bulk-apply-suspects", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ dry_run: true }),
    }).then((r) => r.json());
    const yes = window.confirm(
      `예측 ≠ 라벨 케이스 ${dry.would_apply}건을 일괄 corrections 로 누적할까요?\n\n샘플:\n` +
        (dry.sample || [])
          .map(
            (s) =>
              `  ${s.id}: ${s.label} → ${s.predicted} (${s.keyword || ""})`,
          )
          .join("\n") +
        `\n\n저장 후 "서버 sync" 버튼으로 server 에 push 하면 v5 학습에 반영됩니다.`,
    );
    if (!yes) return;
    const r = await fetch("/api/donut-eval/bulk-apply-suspects", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }).then((r) => r.json());
    alert(`✅ ${r.applied}건 corrections 누적 (총 ${r.total_corrections}건)`);
    await Promise.all([reloadStats(), reloadCorrCount(), reloadList()]);
  };

  const resetPilot = async () => {
    if (
      !window.confirm(
        `Pilot 결과 ${pilotProgress.done}건 모두 삭제?\n다음 호출부터 처음부터 다시 시작.`,
      )
    )
      return;
    await fetch("/api/donut-eval/pilot-reset", { method: "POST" });
    setPilotResult(null);
    await reloadPilotProgress();
  };

  const syncToServer = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const r = await fetch("/api/donut-eval/sync-to-server", {
        method: "POST",
      });
      const j = await r.json();
      if (j.ok) alert(`서버 동기화 완료 (${j.pushed || 0}건)`);
      else alert(`동기화 실패: ${j.error}`);
    } catch (e) {
      alert(`오류: ${e.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const detailOpen = !!selectedId;
  const clearSelection = () => {
    setSelectedId(null);
    setDetail(null);
  };

  // G.2: 저장 완료 후 다음 staged sample 자동 선택 (Q2 fix: snapshot before removal)
  const _moveToNextStaged = useCallback(
    (doneSavedId) => {
      // snapshot 현재 items + index BEFORE removal (Q2 race condition fix)
      const currentItems = items;
      const currentIdx = currentItems.findIndex((it) => it.id === doneSavedId);
      // staged items in current page, excluding the just-saved one
      const stagedInSnapshot = currentItems.filter(
        (it) => recentlyStaged.has(it.id) && it.id !== doneSavedId,
      );
      // prefer staged items AFTER current index; fallback to first staged
      const afterCurrent = stagedInSnapshot.filter((it) => {
        const idx = currentItems.findIndex((x) => x.id === it.id);
        return idx > currentIdx;
      });
      const nextItem =
        afterCurrent.length > 0 ? afterCurrent[0] : stagedInSnapshot[0];
      if (nextItem) {
        setSelectedId(nextItem.id);
        setTimeout(() => {
          const el = itemRefs.current[nextItem.id];
          if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }, 0);
      } else {
        // no more staged — clear selection (user clicks next manually per G.2 spec)
        setSelectedId(null);
      }
      // remove saved id from recentlyStaged
      setRecentlyStaged((prev) => {
        const next = new Set(prev);
        next.delete(doneSavedId);
        return next;
      });
    },
    [items, recentlyStaged],
  );

  // doc_type 카운트 매핑
  const typeCount = (code) => {
    if (!stats) return 0;
    if (code === "ALL") return stats.total || 0;
    return stats.by_type?.[code] || 0;
  };

  const sourceCount = (code) => {
    if (!stats) return null;
    if (code === "all") return null;
    return stats.by_source?.[code] || 0;
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* 헤더 */}
      <div className="px-5 pt-4 pb-2.5 border-b">
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900">
              Donut 학습데이타
            </h3>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
              v4 manifest · server SSH
            </span>
          </div>
          <div className="flex items-center gap-2">
            {corrCount > 0 && (
              <span className="text-xs text-gray-600">
                수정 누적 <b className="text-blue-700">{corrCount}</b>
              </span>
            )}
            <button
              onClick={syncToServer}
              disabled={syncing || corrCount === 0}
              className="text-xs px-2.5 py-1 rounded border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-40 flex items-center gap-1"
              title="로컬 corrections → server eval_labels_corrections.jsonl"
            >
              {syncing ? (
                <>
                  <CloudOff className="w-3 h-3" /> 동기화중...
                </>
              ) : (
                <>
                  <Cloud className="w-3 h-3" /> 서버 sync
                </>
              )}
            </button>
            {pilotProgress.done > 0 && (
              <span className="text-xs text-gray-600">
                Pilot <b className="text-blue-700">{pilotProgress.done}</b>/
                <b>{pilotProgress.total}</b>{" "}
                <button
                  onClick={resetPilot}
                  className="text-[10px] text-rose-500 hover:underline ml-1"
                >
                  리셋
                </button>
              </span>
            )}
            <button
              onClick={runPilotClassify}
              disabled={pilotRunning}
              className="text-xs px-2.5 py-1 rounded border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-40 flex items-center gap-1"
              title="결정론적 순서로 다음 N개 추가 분류. 매 호출은 누적되며 같은 sample 중복 처리 안 함."
            >
              <Sparkles className="w-3 h-3" />{" "}
              {pilotRunning
                ? "실행중..."
                : pilotProgress.done > 0
                  ? "다음 N개 분류"
                  : "Pilot 자동분류"}
            </button>
            <button
              onClick={reloadCache}
              className="text-xs px-2.5 py-1 rounded border border-gray-300 hover:bg-gray-50 flex items-center gap-1 text-gray-700"
            >
              <RotateCcw className="w-3 h-3" /> 서버 reload
            </button>
            {/* G.1: 버튼 통일 — 라벨링 시작, AI 자동확정 버튼 제거 (2026-05-25).
                코드는 보존. 필요 시 주석 해제로 재활성화 가능.
                runBulkExtractConfirm, setLabelingIds 함수는 여전히 사용 가능. */}
            {/* <button
              disabled={selectedIds.size === 0}
              onClick={() => setLabelingIds(Array.from(selectedIds))}
              className="text-xs px-2.5 py-1 rounded border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-40 font-medium"
              title="선택한 샘플을 1건씩 라벨링 모드로 검수"
            >
              선택 {selectedIds.size}건 라벨링 시작
            </button> */}
            {/* <button
              disabled={selectedIds.size === 0 || bulkConfirm.running}
              onClick={runBulkExtractConfirm}
              className="text-xs px-2.5 py-1 rounded border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 disabled:opacity-40 font-medium"
              title="검수 생략, Donut+Gemini 일치 필드만 즉시 저장 (빠름, 50건 미만 권장)"
            >
              {bulkConfirm.running
                ? `처리 중... (${bulkConfirm.total}건)`
                : `선택 ${selectedIds.size}건 AI 자동확정`}
            </button> */}
            <button
              disabled={
                selectedIds.size === 0 || selectedIds.size > 50 || stageRunning
              }
              onClick={runBulkExtractStage}
              className="text-xs px-2.5 py-1 rounded border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-40 font-medium"
              title="선택한 샘플을 Donut+Gemini 추출 → 메인 화면 인라인 검수 (저장 후 자동으로 다음 샘플 선택)"
            >
              {stageRunning
                ? `AI 추출 중... (${selectedIds.size}건)`
                : `선택 ${selectedIds.size}건 AI 검증추출`}
            </button>
            <button
              onClick={() => setTrainModal(true)}
              className="text-xs px-2.5 py-1 rounded border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 font-medium flex items-center gap-1"
              title="confirmed 검수 데이터로 Donut v10 학습 시작"
            >
              v10 학습 시작
            </button>
          </div>
        </div>

        {/* G.4: AI 추출/저장 완료 배너 (alert 대체, 자동 소멸) */}
        {stageBanner && (
          <div
            className={`mt-1 px-3 py-1.5 rounded border text-xs flex items-center justify-between gap-2 ${
              stageBanner.type === "success"
                ? "border-green-300 bg-green-50 text-green-800"
                : "border-blue-200 bg-blue-50 text-blue-800"
            }`}
          >
            <span>{stageBanner.text}</span>
            <button
              onClick={() => setStageBanner(null)}
              className="text-gray-400 hover:text-gray-600 leading-none"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* v10 학습 상태 패널 */}
        {trainJob && (
          <div
            className={`mt-2 px-3 py-2 rounded border text-xs flex flex-wrap items-center gap-3 ${
              trainJob.status === "failed"
                ? "border-red-300 bg-red-50"
                : trainJob.status === "completed" || trainJob.is_complete
                  ? "border-green-400 bg-green-50"
                  : trainJob.status === "skipped" ||
                      trainJob.status === "awaiting_train_script"
                    ? "border-yellow-300 bg-yellow-50"
                    : "border-blue-200 bg-blue-50/40"
            }`}
          >
            <span className="font-semibold text-gray-800">
              {trainJob.status === "completed" || trainJob.is_complete
                ? "v10 학습 완료"
                : "v10 학습 job"}
            </span>
            <span className="font-mono text-gray-600 text-[10px]">
              {trainJob.job_id}
            </span>
            <span
              className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${
                trainJob.status === "failed"
                  ? "bg-red-100 text-red-700"
                  : trainJob.status === "completed" || trainJob.is_complete
                    ? "bg-green-100 text-green-700"
                    : trainJob.status === "skipped" ||
                        trainJob.status === "awaiting_train_script"
                      ? "bg-yellow-100 text-yellow-700"
                      : "bg-blue-100 text-blue-700"
              }`}
            >
              {TRAIN_STATUS_LABELS[trainJob.status] || trainJob.status}
              {trainPolling && " ..."}
            </span>
            {trainJob.confirmed_count != null && (
              <span className="text-gray-600">
                confirmed <b>{trainJob.confirmed_count}</b>건
              </span>
            )}
            {/* Epoch + eval_loss (training / completed) */}
            {(trainJob.status === "training" ||
              trainJob.status === "completed" ||
              trainJob.is_complete) &&
              trainJob.epoch != null && (
                <span className="text-blue-700 font-medium">
                  Epoch: <b>{trainJob.epoch}</b>
                </span>
              )}
            {(trainJob.status === "training" ||
              trainJob.status === "completed" ||
              trainJob.is_complete) &&
              trainJob.eval_loss != null && (
                <span className="text-blue-700 font-medium">
                  eval_loss: <b>{trainJob.eval_loss.toFixed(4)}</b>
                </span>
              )}
            {/* JSONL path for non-training statuses */}
            {trainJob.status !== "training" &&
              trainJob.status !== "completed" &&
              trainJob.jsonl_path && (
                <span
                  className="text-gray-500 text-[10px] truncate max-w-[240px]"
                  title={trainJob.jsonl_path}
                >
                  {trainJob.jsonl_path}
                </span>
              )}
            {/* Error */}
            {trainJob.status === "failed" && trainJob.error && (
              <span
                className="text-red-600 truncate max-w-[300px]"
                title={trainJob.error}
              >
                {trainJob.error}
              </span>
            )}
            {/* Note (awaiting_train_script / skipped) */}
            {trainJob.note &&
              (trainJob.status === "awaiting_train_script" ||
                trainJob.status === "skipped") && (
                <span
                  className="text-yellow-700 text-[10px] italic truncate max-w-[400px]"
                  title={trainJob.note}
                >
                  {trainJob.note}
                </span>
              )}
            {/* Completed: eval + swap button */}
            {(trainJob.status === "completed" || trainJob.is_complete) && (
              <button
                onClick={() => setEvalSwapModal(true)}
                className="px-2 py-0.5 rounded border border-green-400 bg-white text-green-700 text-[11px] font-medium hover:bg-green-50"
              >
                평가 + swap 진행
              </button>
            )}
            <button
              onClick={() => {
                stopTrainPoll();
                setTrainJob(null);
              }}
              className="ml-auto text-gray-400 hover:text-gray-600 text-[11px]"
            >
              닫기
            </button>
          </div>
        )}

        {/* Pilot 결과 표시 */}
        {pilotResult && (
          <div className="mt-2 px-3 py-2 rounded border border-blue-200 bg-blue-50/40 text-xs">
            <div className="font-semibold text-blue-900 mb-1">
              이번 batch — {pilotResult.n}건 (
              {pilotResult.elapsed_sec?.toFixed(1)}s)
              {pilotResult.batch_range && (
                <span className="ml-2 text-gray-600 font-normal">
                  순서 {pilotResult.batch_range.start + 1}~
                  {pilotResult.batch_range.end + 1} /{" "}
                  {pilotResult.batch_range.total}
                </span>
              )}
              {pilotResult.cumulative && (
                <span className="ml-2 text-gray-600 font-normal">
                  · 누적{" "}
                  <b className="text-blue-700">{pilotResult.cumulative.done}</b>
                  /{pilotResult.cumulative.total}
                </span>
              )}
            </div>
            {pilotResult.stats && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-gray-700">
                {pilotResult.stats.keyword_ok > 0 && (
                  <div>
                    keyword == 라벨:{" "}
                    <b className="text-blue-700">
                      {pilotResult.stats.keyword_eq_label}/{pilotResult.n}
                    </b>{" "}
                    ({pilotResult.stats.keyword_label_match_pct ?? "-"}%)
                  </div>
                )}
                {pilotResult.stats.donut_ok > 0 && (
                  <div>
                    donut == 라벨:{" "}
                    <b>
                      {pilotResult.stats.donut_eq_label}/{pilotResult.n}
                    </b>{" "}
                    ({pilotResult.stats.donut_label_match_pct ?? "-"}%)
                  </div>
                )}
                {pilotResult.stats.gemini_ok > 0 && (
                  <div>
                    gemini == 라벨:{" "}
                    <b>
                      {pilotResult.stats.gemini_eq_label}/{pilotResult.n}
                    </b>{" "}
                    ({pilotResult.stats.gemini_label_match_pct ?? "-"}%)
                  </div>
                )}
                {pilotResult.stats.keyword_ok > 0 &&
                  pilotResult.stats.gemini_ok > 0 && (
                    <div>
                      keyword == gemini:{" "}
                      <b>
                        {pilotResult.stats.keyword_eq_gemini}/{pilotResult.n}
                      </b>
                    </div>
                  )}
              </div>
            )}
            {pilotResult.distributions && (
              <div className="mt-1 text-[10px] text-gray-600 space-y-0.5">
                <div>
                  label 분포:{" "}
                  {Object.entries(pilotResult.distributions.label || {})
                    .map(([k, v]) => `${k}:${v}`)
                    .join(" · ")}
                </div>
                {Object.keys(pilotResult.distributions.keyword || {}).length >
                  0 && (
                  <div>
                    keyword 분포:{" "}
                    {Object.entries(pilotResult.distributions.keyword || {})
                      .map(([k, v]) => `${k}:${v}`)
                      .join(" · ")}
                  </div>
                )}
                {Object.keys(pilotResult.distributions.donut || {}).length >
                  0 && (
                  <div>
                    donut 분포:{" "}
                    {Object.entries(pilotResult.distributions.donut || {})
                      .map(([k, v]) => `${k}:${v}`)
                      .join(" · ")}
                  </div>
                )}
                {Object.keys(pilotResult.distributions.gemini || {}).length >
                  0 && (
                  <div>
                    gemini 분포:{" "}
                    {Object.entries(pilotResult.distributions.gemini || {})
                      .map(([k, v]) => `${k}:${v}`)
                      .join(" · ")}
                  </div>
                )}
              </div>
            )}
            {pilotResult.disagreement_examples?.length > 0 && (
              <details className="mt-1.5">
                <summary className="cursor-pointer text-amber-700 text-[11px]">
                  의심 케이스 (예측 ≠ label){" "}
                  {pilotResult.disagreement_examples.length}개 예시
                </summary>
                <div className="mt-1 space-y-0.5 text-[10px] text-gray-700 font-mono">
                  {pilotResult.disagreement_examples.map((d, i) => (
                    <div key={i}>
                      {d.id}: <b className="text-gray-500">{d.label}</b> →
                      {d.keyword && (
                        <>
                          {" "}
                          kw <b className="text-blue-700">{d.keyword}</b>
                          {d.keyword_match && (
                            <span className="text-gray-400">
                              ({d.keyword_match})
                            </span>
                          )}
                        </>
                      )}
                      {d.donut && (
                        <>
                          {" "}
                          · donut <b>{d.donut}</b>
                        </>
                      )}
                      {d.gemini && (
                        <>
                          {" "}
                          · gemini <b className="text-amber-700">{d.gemini}</b>
                        </>
                      )}
                      {d.gemini_reason && (
                        <span className="text-gray-500">
                          {" "}
                          ({d.gemini_reason.slice(0, 50)})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
        {stats && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-700">
            <span>
              전체 <b className="text-gray-900">{stats.total}</b>
            </span>
            <span>
              train <b className="text-gray-900">{stats.counts?.train ?? 0}</b>{" "}
              / val <b className="text-blue-700">{stats.counts?.val ?? 0}</b> /
              test <b className="text-gray-900">{stats.counts?.test ?? 0}</b>
            </span>
            <span>
              검수 확정{" "}
              <b className="text-blue-700">{stats.confirmed_count ?? 0}</b>
            </span>
            <span>
              v2 distill{" "}
              <b className="text-gray-900">
                {stats.by_source?.v2_distill ?? 0}
              </b>
            </span>
            {stats.loaded_at && (
              <span className="text-gray-400 text-xs">
                로드 {new Date(stats.loaded_at).toLocaleTimeString()}
              </span>
            )}
          </div>
        )}
        {/* 🆕 keyword 분류 통계 + 일괄 확정 */}
        {stats?.keyword_classify?.processed > 0 && (
          <div className="mt-2 px-3 py-2 rounded border border-blue-200 bg-blue-50/40 text-xs flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-semibold text-blue-900">키워드 분류</span>
            <span>
              처리 <b>{stats.keyword_classify.processed}</b>/{stats.total}
            </span>
            <span>
              일치{" "}
              <b className="text-gray-700">{stats.keyword_classify.matches}</b>
            </span>
            <span>
              ⚠️ suspect{" "}
              <b className="text-amber-700">{stats.keyword_classify.suspect}</b>
            </span>
            <span>
              no_match{" "}
              <b className="text-gray-500">{stats.keyword_classify.no_match}</b>
            </span>
            {stats.keyword_classify.change_matrix?.length > 0 && (
              <details className="basis-full">
                <summary className="cursor-pointer text-blue-700 text-[11px]">
                  변경 분포 top {stats.keyword_classify.change_matrix.length}
                </summary>
                <div className="mt-1 text-[11px] text-gray-700 grid grid-cols-3 gap-x-3">
                  {stats.keyword_classify.change_matrix.map((c, i) => (
                    <span key={i}>
                      {c.change}: <b>{c.count}</b>
                    </span>
                  ))}
                </div>
              </details>
            )}
            {stats.keyword_classify.suspect > 0 && (
              <button
                onClick={bulkApplySuspects}
                className="ml-auto px-2 py-1 rounded bg-amber-500 text-white hover:bg-amber-600 text-[11px] font-medium"
              >
                ⚠️ {stats.keyword_classify.suspect}건 일괄 corrections 확정
              </button>
            )}
          </div>
        )}
      </div>

      {/* doc_type 탭 */}
      <div className="px-5 py-2 border-b flex flex-wrap gap-1">
        {DOC_TYPES.map((d) => {
          const active = docType === d.code;
          const cnt = typeCount(d.code);
          return (
            <button
              key={d.code}
              onClick={() => {
                setDocType(d.code);
                setPage(1);
              }}
              className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
                active
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-gray-50 text-gray-700 hover:bg-gray-100"
              }`}
            >
              {d.name}
              {cnt > 0 && (
                <span
                  className={`ml-1.5 text-[10px] tabular-nums ${active ? "text-blue-100" : "text-gray-500"}`}
                >
                  {cnt}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* 필터 */}
      <div className="px-5 py-2 border-b flex flex-wrap gap-2 items-center">
        <select
          value={split}
          onChange={(e) => {
            setSplit(e.target.value);
            setPage(1);
          }}
          className="text-xs px-2 py-1 border border-gray-200 rounded"
        >
          {SPLIT_FILTERS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <select
          value={source}
          onChange={(e) => {
            setSource(e.target.value);
            setPage(1);
          }}
          className="text-xs px-2 py-1 border border-gray-200 rounded"
        >
          {SOURCE_FILTERS.map((s) => {
            const c = sourceCount(s.value);
            return (
              <option key={s.value} value={s.value}>
                {s.label}
                {c != null ? ` (${c})` : ""}
              </option>
            );
          })}
        </select>
        <select
          value={classify}
          onChange={(e) => {
            setClassify(e.target.value);
            setPage(1);
          }}
          className="text-xs px-2 py-1 border border-gray-200 rounded"
        >
          {CLASSIFY_FILTERS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <div className="ml-auto flex items-center gap-1">
          <Search className="w-3.5 h-3.5 text-gray-400" />
          <input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setQ(qInput);
                setPage(1);
              }
            }}
            placeholder="검색 (id, GT 값)"
            className="text-xs px-2 py-1 border border-gray-200 rounded w-56 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 outline-none"
          />
          <button
            onClick={() => {
              setQ(qInput);
              setPage(1);
            }}
            className="text-xs px-2.5 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
          >
            검색
          </button>
        </div>
      </div>

      {/* 본문: 리스트 + 이미지 + 폼 */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        <div
          className={`${detailOpen ? "w-[300px]" : "flex-1"} flex flex-col border-r min-w-0`}
        >
          <div className="flex-1 overflow-auto">
            {loading ? (
              <div className="p-8 text-sm text-gray-400 text-center">
                로딩...
              </div>
            ) : items.length === 0 ? (
              <div className="p-8 text-sm text-gray-400 text-center">
                결과 없음 (서버 reload 필요할 수 있음)
              </div>
            ) : detailOpen ? (
              <div>
                {/* Compact list toolbar: 전체선택 / 해제 + 선택 수 표시 */}
                <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-3 py-1.5 flex items-center gap-2 text-xs">
                  <button
                    onClick={toggleSelectAll}
                    className="px-2 py-0.5 rounded border border-gray-300 hover:bg-gray-50 text-gray-700"
                    title="현재 페이지의 모든 sample 선택/해제"
                  >
                    {items.length > 0 &&
                    items.every((it) => selectedIds.has(it.id))
                      ? "전체 해제"
                      : "전체 선택"}
                  </button>
                  {selectedIds.size > 0 && (
                    <button
                      onClick={() => setSelectedIds(new Set())}
                      className="px-2 py-0.5 rounded border border-gray-200 hover:bg-gray-50 text-gray-500"
                      title="모든 선택 해제 (다른 페이지 포함)"
                    >
                      모두 해제
                    </button>
                  )}
                  <span className="ml-auto text-gray-500">
                    {selectedIds.size > 0
                      ? `${selectedIds.size}건 선택`
                      : `${items.length}건`}
                  </span>
                </div>
                {items.map((it) => (
                  <div
                    key={it.id}
                    ref={(el) => {
                      if (el) itemRefs.current[it.id] = el;
                      else delete itemRefs.current[it.id];
                    }}
                    onClick={() => setSelectedId(it.id)}
                    className={`px-3 py-2 border-b border-gray-100 cursor-pointer transition-colors ${
                      selectedId === it.id
                        ? "bg-blue-50 border-l-2 border-l-blue-500 -ml-px pl-[11px]"
                        : selectedIds.has(it.id)
                          ? "bg-indigo-50"
                          : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <span onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(it.id)}
                          onChange={() => toggleSelect(it.id)}
                        />
                      </span>
                      <span className="text-xs font-mono text-gray-800 truncate flex-1">
                        {it.id}
                      </span>
                      {it.suspect && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium"
                          title={`예측: ${it.predicted_doc_type} (${it.matched_keyword || ""})`}
                        >
                          ⚠️ {it.predicted_doc_type}
                        </span>
                      )}
                      {it.no_match && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500"
                          title="키워드 매칭 실패 (OCR 품질 낮음)"
                        >
                          ?
                        </span>
                      )}
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded ${
                          it.source === "user_label"
                            ? "bg-amber-50 text-amber-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {it.source === "user_label" ? "👤" : it.source}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-gray-500">
                      {it.suspect ? (
                        <span
                          className="px-1.5 py-0 rounded bg-amber-50 text-amber-800 border border-amber-200"
                          title={`키워드: ${it.matched_keyword || "-"}`}
                        >
                          {it.doc_type} → <b>{it.predicted_doc_type}</b>
                        </span>
                      ) : (
                        <span className="px-1.5 py-0 rounded bg-gray-100 text-gray-700">
                          {it.doc_type}
                        </span>
                      )}
                      <span>{it.split}</span>
                      <span className="ml-auto">{it.field_count} 필드</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr className="text-left text-gray-600">
                    <th className="px-2 py-2 w-8">
                      <input
                        type="checkbox"
                        checked={
                          items.length > 0 &&
                          items.every((it) => selectedIds.has(it.id))
                        }
                        onChange={toggleSelectAll}
                        title="전체 선택/해제"
                      />
                    </th>
                    <th className="px-3 py-2">ID</th>
                    <th className="px-2 py-2 w-24">유형</th>
                    <th className="px-2 py-2 w-14">split</th>
                    <th className="px-2 py-2 w-28">source</th>
                    <th className="px-2 py-2 w-16">필드</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr
                      key={it.id}
                      ref={(el) => {
                        if (el) itemRefs.current[it.id] = el;
                        else delete itemRefs.current[it.id];
                      }}
                      onClick={() => setSelectedId(it.id)}
                      className={`border-b border-gray-100 cursor-pointer transition-colors ${selectedId === it.id ? "bg-blue-50" : selectedIds.has(it.id) ? "bg-indigo-50" : recentlyProcessed.has(it.id) ? "bg-green-50" : recentlyStaged.has(it.id) ? "bg-yellow-50" : "hover:bg-gray-50"}`}
                      style={
                        recentlyProcessed.has(it.id)
                          ? { borderLeft: "3px solid #16a34a" }
                          : recentlyStaged.has(it.id)
                            ? { borderLeft: "3px solid #eab308" }
                            : undefined
                      }
                    >
                      <td
                        className="px-2 py-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.has(it.id)}
                          onChange={() => toggleSelect(it.id)}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono">{it.id}</td>
                      <td className="px-2 py-2">
                        {it.suspect ? (
                          <span
                            className="text-[11px] px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200"
                            title={`키워드: ${it.matched_keyword || "-"}`}
                          >
                            {it.doc_type} → <b>{it.predicted_doc_type}</b>
                          </span>
                        ) : (
                          <span className="text-[11px] px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                            {it.doc_type}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded ${it.split === "val" ? "bg-blue-100 text-blue-700" : "bg-gray-50 text-gray-500"}`}
                        >
                          {it.split}
                        </span>
                      </td>
                      <td className="px-2 py-2">
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded ${
                            it.source === "user_label"
                              ? "bg-amber-50 text-amber-700"
                              : "bg-gray-50 text-gray-600"
                          }`}
                        >
                          {it.source}
                        </span>
                      </td>
                      <td className="px-2 py-2 tabular-nums text-gray-700">
                        {it.field_count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="px-3 py-2 border-t flex items-center justify-between text-xs text-gray-700 bg-white">
            <div>
              총 <b>{total}</b>건 · {page}/{totalPages}p
            </div>
            <div className="flex gap-1">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-2 py-1 rounded border border-gray-300 disabled:opacity-30 hover:bg-gray-50"
              >
                이전
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-2 py-1 rounded border border-gray-300 disabled:opacity-30 hover:bg-gray-50"
              >
                다음
              </button>
            </div>
          </div>
        </div>

        {detailOpen && (
          <ImageViewer
            url={detail?.image_url || null}
            onClose={clearSelection}
          />
        )}

        {detailOpen && detail && (
          <FormPanel
            sample={detail}
            onClose={clearSelection}
            onSaved={(info) => {
              // G.3: confirmed_count 갱신
              reloadCorrCount();
              reloadStats();

              const savedId = info?.savedId || info?.id;
              if (savedId) {
                if (info?.deleted) {
                  // 삭제: list에서 즉시 제거 + total 감소
                  setItems((prev) => prev.filter((it) => it.id !== savedId));
                  setTotal((prev) => Math.max(0, prev - 1));
                  clearSelection();
                } else {
                  // G.2: 저장 후 list 자동 갱신
                  // core_missing 필터에서는 채운 sample 즉시 제거
                  if (classify === "core_missing") {
                    setItems((prev) => prev.filter((it) => it.id !== savedId));
                    setTotal((prev) => Math.max(0, prev - 1));
                  }
                  // G.4: 배너 표시 (alert 대체)
                  setStageBanner({
                    text: `저장 완료 (변경 필드 ${info?.changed_count ?? 0})`,
                    type: "success",
                  });
                  setTimeout(() => setStageBanner(null), 3000);
                  // G.2: 다음 staged sample 자동 선택 (Q2 fix 적용)
                  _moveToNextStaged(savedId);
                  // list 서버 재동기화 (DB 상태 반영)
                  reloadList();
                }
              }
            }}
            aiReviewMeta={detail?.correction?.ai_review_meta || null}
            correctionStatus={detail?.correction?.status || null}
          />
        )}
      </div>

      {/* 라벨링 모드 — full-screen overlay */}
      {labelingIds && (
        <DonutLabelingMode
          sampleIds={labelingIds}
          onExit={() => {
            setLabelingIds(null);
            setSelectedIds(new Set());
          }}
          onSaved={() => reloadCorrCount()}
        />
      )}

      {/* AI 검증추출 모달 — 미사용 (Phase E: 인라인 워크플로우로 전환). 보존만. */}
      {/* {bulkVerifyIds && (
        <DonutLabelingBulkVerify
          sampleIds={bulkVerifyIds}
          onClose={() => setBulkVerifyIds(null)}
          onSavedBatch={(savedIds) => {
            const okIds = new Set(savedIds);
            setRecentlyProcessed(okIds);
            setTimeout(() => setRecentlyProcessed(new Set()), 3000);
            setSelectedIds(new Set());
            setBulkVerifyIds(null);
            Promise.all([reloadCorrCount(), reloadList()]);
          }}
        />
      )} */}

      {/* v10 학습 시작 확인 모달 */}
      {trainModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-[420px] p-6">
            <h4 className="text-base font-semibold text-gray-900 mb-1">
              Donut v10 학습 시작
            </h4>
            <p className="text-xs text-gray-600 mb-4 leading-relaxed">
              현재 <b>confirmed</b> 상태 샘플을 JSONL 로 빌드한 뒤 vast.ai 77
              pod 에 SCP 업로드 + SSH 킥오프 합니다.
              <br />
              <span className="text-amber-700 font-medium">
                사전 조건:
              </span>{" "}
              <code className="text-[11px] bg-gray-100 px-1 py-0.5 rounded">
                /workspace/donut_train_v10.py
              </code>{" "}
              가 vast.ai 에 존재해야 합니다. 없으면{" "}
              <code className="text-[11px] bg-gray-100 px-1 py-0.5 rounded">
                awaiting_train_script
              </code>{" "}
              상태로 종료됩니다.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setTrainModal(false)}
                className="px-4 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50 text-gray-700"
              >
                취소
              </button>
              <button
                onClick={triggerTrain}
                className="px-4 py-1.5 text-sm rounded bg-green-600 text-white hover:bg-green-700 font-medium"
              >
                학습 트리거
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 평가 + swap 안내 모달 */}
      {evalSwapModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-[480px] p-6">
            <h4 className="text-base font-semibold text-gray-900 mb-3">
              평가 + swap 진행 방법
            </h4>
            <ol className="text-xs text-gray-700 space-y-2 leading-relaxed list-decimal list-inside">
              <li>
                vast.ai 77 pod SSH 접속{" "}
                <code className="bg-gray-100 px-1 py-0.5 rounded text-[11px]">
                  ssh -p 40567 root@116.120.230.77
                </code>
              </li>
              <li>
                평가 스크립트 실행 (v9 템플릿 참고):
                <br />
                <code className="bg-gray-100 px-1 py-0.5 rounded text-[11px] block mt-1">
                  cd /workspace && python donut_eval_v8_v9.py
                  --model_path=/root/donut_train_v10/output/.../final
                  --split=val
                </code>
              </li>
              <li>
                eval 결과 확인 후 swap 결정 — 기존 v9 경로 백업:
                <br />
                <code className="bg-gray-100 px-1 py-0.5 rounded text-[11px] block mt-1">
                  cp -r /root/donut_train_v9/output/.../final
                  /root/donut_train_v9_backup
                </code>
              </li>
              <li>
                backend .env 의{" "}
                <code className="bg-gray-100 px-1 py-0.5 rounded text-[11px]">
                  DONUT_MODEL_PATH
                </code>{" "}
                를 v10 경로로 업데이트 후 서버 재시작.
              </li>
            </ol>
            <p className="text-[11px] text-gray-400 mt-3 italic">
              자동 swap 은 B-mini 범위 밖입니다. 위 절차를 수동으로 진행하세요.
            </p>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setEvalSwapModal(false)}
                className="px-4 py-1.5 text-sm rounded bg-gray-800 text-white hover:bg-gray-700"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
